import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  CellContextMenuEvent,
  ColDef,
  GridApi,
  IRowNode,
  RowStyle,
  RowClassParams,
} from 'ag-grid-community';
import {
  ArrowRight,
  ClipboardCopy,
  ClipboardPaste,
  FileSpreadsheet,
  Paintbrush,
  SlidersHorizontal,
  Wand2,
} from 'lucide-react';
import {
  LopGridContextMenu,
  type LopMenuItem,
} from '../components/LopGridContextMenu';
import { exportXlsx, type XlsxColumn } from '../api/export';
import { navActionsFor } from '../navigation/gridNavRegistry';
import { t, type MessageKey } from '../i18n/t';
import {
  SUMMARY_OPTIONS,
  type ColumnCustomization,
} from '../types/UserCustomization';

// Soft, readable row tones. CSS-variable-friendly: the variables are defined
// in LopGridContextMenu.css (:root); the literal is the fallback.
const ROW_COLOR_GREEN = 'var(--lop-row-color-green, #dcfce7)';
const ROW_COLOR_YELLOW = 'var(--lop-row-color-yellow, #fef9c3)';

interface QuickFormatDef {
  labelKey: MessageKey;
  format: string | null;
  signColoring: boolean;
}

const QUICK_FORMATS: QuickFormatDef[] = [
  { labelKey: 'contextMenu.quickFormatDecimal', format: '#,##0.00', signColoring: false },
  { labelKey: 'contextMenu.quickFormatInteger', format: '#,##0', signColoring: false },
  { labelKey: 'contextMenu.quickFormatIntegerSigned', format: '#,##0', signColoring: true },
  { labelKey: 'contextMenu.quickFormatPercent', format: '0.00%', signColoring: false },
  { labelKey: 'contextMenu.quickFormatPercentSigned', format: '0.00%', signColoring: true },
  { labelKey: 'contextMenu.quickFormatMonth', format: 'MM/yy', signColoring: false },
  { labelKey: 'contextMenu.quickFormatNone', format: null, signColoring: false },
];

// Custom right-click context menu hook. Extracted from LopCrudPage so any
// non-LopCrudPage screen (e.g. HoldingsPage) can wire the same menu by
// passing its own grid API ref, columnDefs, and resourceCode. The hook owns
// the menu state and renders <LopGridContextMenu> — the caller drops the
// returned `contextMenu` node into its tree and wires `onCellContextMenu`
// onto its <LopAgGrid>.
//
// The hook deliberately does NOT own the customization dialog state — that
// stays in the consumer page because the dialog needs the page's own
// useUserCustomization wiring. The hook just invokes `onCustomize(...)` so
// the page can open its dialog with the captured sample row + initial field.
//
// `extraItems` is the extension point for page-specific menu items
// (e.g. HoldingsPage's "פירוט חישובים" → open the CalcDetailsModal). The
// builder is called per right-click with the cell context so the page can
// decide whether/which extras to show. See the prop docs below.

interface MenuContext<TData> {
  x: number;
  y: number;
  colId: string | undefined;
  value: unknown;
  rowData: Record<string, unknown> | null;
  node: IRowNode<TData> | null;
}

export interface UseGridContextMenuOptions<TData> {
  /** Ref to the grid's GridApi (filled in onGridReady). */
  gridApiRef: React.RefObject<GridApi<TData> | null>;
  /** Page's base column defs — used for the xlsx export header list. */
  columnDefs: ColDef<TData>[];
  /** Used for navigation registry lookup AND the export sheet-name fallback. */
  resourceCode: string;
  /** i18n key for the page title; used as the export sheet name. */
  titleKey: MessageKey;
  /** Strip internal row fields (e.g. __tempId/__status/__deleted) before
   *  copy/export/navigate. LopCrudPage passes a stripper; read-only screens
   *  with no internal fields can omit this. */
  stripRowInternals?: (row: Record<string, unknown>) => Record<string, unknown>;
  /** When provided, enables the "Paste" item. The callback receives the
   *  target column id, the clipboard text, and the row node so the caller
   *  can wire `node.setDataValue(colId, value)` (the LopCrudPage flow). */
  onPaste?: (
    colId: string,
    value: string,
    node: IRowNode<TData>,
  ) => void;
  /** When provided, enables the "Customize" item. The callback receives the
   *  row data (or null when no row was clicked) and the column id (or null)
   *  so the dialog can preview the right cell and open on the right column. */
  onCustomize?: (
    sampleRow: Record<string, unknown> | null,
    colId: string | null,
  ) => void;
  /** When provided (together with `getColumnCustomization`), enables the
   *  "עיצוב מהיר" submenu — a shortcut to the customization dialog (applied on screen, persisted only by the status-bar save button)
   *  for the right-clicked column. The consumer merges `patch` into
   *  `columns[colId]` of its current customization and saves it. Must be
   *  memoized (useCallback). */
  onQuickCustomize?: (colId: string, patch: Partial<ColumnCustomization>) => void;
  /** Current saved customization of a column (drives the check marks in the
   *  quick-format submenu). Must be memoized (useCallback). */
  getColumnCustomization?: (colId: string) => ColumnCustomization | undefined;
  /** Page-specific extra items appended to the menu, after the built-in
   *  items (copy/paste/export/navigation) but BEFORE the "Customize" group.
   *  Receives the clicked-cell context so the page can decide per-row whether
   *  to render an item (e.g. only when a row was clicked, only for certain
   *  column ids, etc.). Return an empty array to render nothing.
   *
   *  Must be memoized via `useCallback` — the menuItems memo depends on it,
   *  and a fresh identity on every render would defeat the optimization.
   *
   *  Example (HoldingsPage adds a "פירוט חישובים" item that opens a modal):
   *  ```ts
   *  extraItems: useCallback(({ rowData }) => {
   *    if (!rowData) return [];
   *    return [{
   *      label: t('holdings.contextMenu.calcDetails'),
   *      icon: <Calculator size={16} />,
   *      onClick: () => openCalcDetails(rowData as unknown as Holding),
   *    }];
   *  }, [openCalcDetails])
   *  ``` */
  extraItems?: (ctx: {
    rowData: Record<string, unknown> | null;
    colId: string | null;
    value: unknown;
    node: IRowNode<TData> | null;
  }) => LopMenuItem[];
}

export interface UseGridContextMenuResult<TData> {
  onCellContextMenu: (event: CellContextMenuEvent<TData>) => void;
  /** Drop this into the consumer's JSX — null when no menu is open. */
  contextMenu: React.ReactNode;
  /** Pass to <LopAgGrid getRowStyle>. Stable identity (reads a ref). Paints
   *  the EPHEMERAL manual row colors set via "צבע שורה". Dirty-state classes
   *  (`!important` in LopCrudPage.css) outrank this inline style by design. */
  getRowStyle: (params: RowClassParams<TData>) => RowStyle | undefined;
  /** Forget all manual row colors and repaint. Call on every data reload. */
  clearRowColors: () => void;
}

export function useGridContextMenu<TData>(
  opts: UseGridContextMenuOptions<TData>,
): UseGridContextMenuResult<TData> {
  const {
    gridApiRef,
    columnDefs,
    resourceCode,
    titleKey,
    stripRowInternals,
    onPaste,
    onCustomize,
    onQuickCustomize,
    getColumnCustomization,
    extraItems,
  } = opts;

  const navigate = useNavigate();
  const [menuContext, setMenuContext] = useState<MenuContext<TData> | null>(null);

  const onCellContextMenu = useCallback(
    (event: CellContextMenuEvent<TData>) => {
      // params.event is the native MouseEvent; suppress the browser menu and
      // capture the cell context so we can open our own menu.
      const mouseEvent = event.event as MouseEvent | null;
      if (!mouseEvent) return;
      mouseEvent.preventDefault();
      lastPosRef.current = { x: mouseEvent.clientX, y: mouseEvent.clientY };
      const rawData = event.data
        ? (event.data as unknown as Record<string, unknown>)
        : null;
      const data =
        rawData == null
          ? null
          : stripRowInternals
            ? stripRowInternals(rawData)
            : rawData;
      setMenuContext({
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
        colId: event.column?.getColId(),
        value: event.value,
        rowData: data,
        node: event.node,
      });
    },
    [stripRowInternals],
  );

  const closeMenu = useCallback(() => setMenuContext(null), []);

  // ---- Ephemeral row colors (never persisted) -----------------------------
  // Keyed by AgGrid node.id. Lives in a ref so getRowStyle keeps a stable
  // identity (a new prop identity would make LopAgGrid reset grid state);
  // repainting is explicit via api.redrawRows.
  const rowColorsRef = useRef<Map<string, string>>(new Map());
  const colorInputRef = useRef<HTMLInputElement>(null);
  const colorTargetsRef = useRef<IRowNode<TData>[]>([]);
  const lastPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const getRowStyle = useCallback(
    (params: RowClassParams<TData>): RowStyle | undefined => {
      const id = params.node.id;
      if (id == null) return undefined;
      const color = rowColorsRef.current.get(id);
      return color ? { backgroundColor: color } : undefined;
    },
    [],
  );

  const applyRowColor = useCallback(
    (nodes: IRowNode<TData>[], color: string | null) => {
      const map = rowColorsRef.current;
      for (const n of nodes) {
        if (n.id == null) continue;
        if (color) map.set(n.id, color);
        else map.delete(n.id);
      }
      gridApiRef.current?.redrawRows({ rowNodes: nodes });
    },
    [gridApiRef],
  );

  const clearRowColors = useCallback(() => {
    const map = rowColorsRef.current;
    if (map.size === 0) return;
    const api = gridApiRef.current;
    const nodes: IRowNode<TData>[] = [];
    if (api) {
      Array.from(map.keys()).forEach((id) => {
        const n = api.getRowNode(id);
        if (n) nodes.push(n);
      });
    }
    map.clear();
    if (api && nodes.length > 0) api.redrawRows({ rowNodes: nodes });
  }, [gridApiRef]);

  const handleCustomColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      applyRowColor(colorTargetsRef.current, e.target.value);
    },
    [applyRowColor],
  );

  const menuItems = useMemo<LopMenuItem[]>(() => {
    if (!menuContext) return [];
    const { colId, value, rowData, node } = menuContext;
    const items: LopMenuItem[] = [];

    // Copy — always enabled.
    items.push({
      label: t('contextMenu.copy'),
      icon: <ClipboardCopy size={16} />,
      onClick: () => {
        void navigator.clipboard.writeText(value == null ? '' : String(value));
      },
    });

    // Paste — only when the consumer provided a paste handler.
    if (onPaste) {
      items.push({
        label: t('contextMenu.paste'),
        icon: <ClipboardPaste size={16} />,
        onClick: () => {
          if (!node || !colId) return;
          void navigator.clipboard
            .readText()
            .then((text) => onPaste(colId, text, node));
        },
      });
    }

    // Export to xlsx — the currently-displayed rows (filter/sort respected)
    // and the page's data columns (each with a field).
    items.push({
      label: t('contextMenu.exportXlsx'),
      icon: <FileSpreadsheet size={16} />,
      onClick: () => {
        const api = gridApiRef.current;
        if (!api) return;
        const rows: Record<string, unknown>[] = [];
        api.forEachNodeAfterFilterAndSort((n) => {
          if (n.data) {
            const raw = n.data as unknown as Record<string, unknown>;
            rows.push(stripRowInternals ? stripRowInternals(raw) : raw);
          }
        });
        const columns: XlsxColumn[] = (columnDefs as ColDef<TData>[])
          .filter((c): c is ColDef<TData> & { field: string } => Boolean(c.field))
          .map((c) => ({
            field: c.field as string,
            header: c.headerName ?? (c.field as string),
          }));
        const sheetName = t(titleKey) || resourceCode;
        void exportXlsx(sheetName, columns, rows);
      },
    });

    // Navigation actions — only when a row was clicked.
    if (rowData) {
      for (const action of navActionsFor(resourceCode)) {
        items.push({
          label: t(action.labelKey),
          icon: <ArrowRight size={16} />,
          onClick: () => navigate(action.to(rowData)),
        });
      }
    }

    // Page-specific extras — appended AFTER the built-in items and BEFORE
    // the Customize group. The page decides per-context whether to emit any.
    if (extraItems) {
      const extras = extraItems({
        rowData,
        colId: colId ?? null,
        value,
        node,
      });
      if (extras.length > 0) {
        items.push(...extras);
      }
    }

    // Row color — EPHEMERAL. Only for a real data row (not the pinned summary
    // row). Targets: all selected rows when the clicked row is selected,
    // otherwise just the clicked row.
    let groupStarted = false;
    const startGroup = (): boolean => {
      const first = !groupStarted;
      groupStarted = true;
      return first;
    };
    if (rowData && node && !node.rowPinned) {
      const targets = (): IRowNode<TData>[] => {
        const api = gridApiRef.current;
        if (node.isSelected() && api) {
          const sel = api.getSelectedNodes();
          if (sel.length > 0) return sel;
        }
        return [node];
      };
      const hasColor = targets().some(
        (n) => n.id != null && rowColorsRef.current.has(n.id),
      );
      const swatch = (bg?: string) => (
        <span
          className={
            'lop-grid-context-menu__swatch' +
            (bg ? '' : ' lop-grid-context-menu__swatch--custom')
          }
          style={bg ? { background: bg } : undefined}
        />
      );
      const rowColorChildren: LopMenuItem[] = [
        {
          label: t('contextMenu.rowColorGreen'),
          icon: swatch(ROW_COLOR_GREEN),
          onClick: () => applyRowColor(targets(), ROW_COLOR_GREEN),
        },
        {
          label: t('contextMenu.rowColorYellow'),
          icon: swatch(ROW_COLOR_YELLOW),
          onClick: () => applyRowColor(targets(), ROW_COLOR_YELLOW),
        },
        {
          label: t('contextMenu.rowColorCustom'),
          icon: swatch(),
          onClick: () => {
            // The hidden <input type="color"> lives outside the menu (the menu
            // unmounts right after this click); click() runs inside the user
            // gesture so the native picker opens.
            colorTargetsRef.current = targets();
            colorInputRef.current?.click();
          },
        },
      ];
      if (hasColor) {
        rowColorChildren.push({
          label: t('contextMenu.rowColorClear'),
          dividerBefore: true,
          onClick: () => applyRowColor(targets(), null),
        });
      }
      items.push({
        label: t('contextMenu.rowColor'),
        icon: <Paintbrush size={16} />,
        dividerBefore: startGroup(),
        children: rowColorChildren,
      });
    }

    // Quick format — PERSISTED shortcut (merges into the saved customization).
    if (
      onQuickCustomize &&
      colId &&
      (columnDefs as ColDef<TData>[]).some((c) => c.field === colId)
    ) {
      const current = getColumnCustomization?.(colId);
      const curFormat = current?.format ?? null;
      const curSign = Boolean(current?.signColoring);
      const curSummary = current?.summary ?? null;
      const formatChildren: LopMenuItem[] = QUICK_FORMATS.map((q) => ({
        label: t(q.labelKey),
        // Sign-colored options preview both signs side by side (green
        // positive / red negative) so the effect is visible before choosing.
        trailing:
          q.format && q.signColoring ? (
            <>
              <span className="lop-grid-context-menu__trailing-positive">{q.format}</span>
              <span className="lop-grid-context-menu__trailing-negative">-{q.format}</span>
            </>
          ) : (
            q.format ?? undefined
          ),
        checked: curFormat === q.format && curSign === q.signColoring,
        onClick: () =>
          onQuickCustomize(colId, { format: q.format, signColoring: q.signColoring }),
      }));
      formatChildren.push({
        label: t('contextMenu.summaryRow'),
        dividerBefore: true,
        children: [
          ...SUMMARY_OPTIONS.map((o) => ({
            label: o.label,
            checked: curSummary === o.value,
            onClick: () => onQuickCustomize(colId, { summary: o.value }),
          })),
          {
            label: t('contextMenu.summaryNone'),
            checked: curSummary === null,
            onClick: () => onQuickCustomize(colId, { summary: null }),
          },
        ],
      });
      items.push({
        label: t('contextMenu.quickFormat'),
        icon: <Wand2 size={16} />,
        dividerBefore: startGroup(),
        children: formatChildren,
      });
    }

    // Customization — only when the consumer wires it (it owns the dialog).
    if (onCustomize) {
      items.push({
        label: t('contextMenu.customize'),
        icon: <SlidersHorizontal size={16} />,
        dividerBefore: startGroup(),
        onClick: () => {
          onCustomize(rowData, colId ?? null);
        },
      });
    }

    return items;
  }, [
    menuContext,
    onPaste,
    onCustomize,
    onQuickCustomize,
    getColumnCustomization,
    applyRowColor,
    extraItems,
    columnDefs,
    titleKey,
    resourceCode,
    navigate,
    gridApiRef,
    stripRowInternals,
  ]);

  // The color input is always mounted (even with the menu closed) so the
  // native picker survives the menu unmounting. Parked near the last click so
  // the picker pops up next to where the user was.
  const contextMenu = (
    <>
      {menuContext && (
        <LopGridContextMenu
          x={menuContext.x}
          y={menuContext.y}
          items={menuItems}
          onClose={closeMenu}
        />
      )}
      <input
        ref={colorInputRef}
        type="color"
        tabIndex={-1}
        aria-label={t('contextMenu.rowColorPickerLabel')}
        onChange={handleCustomColorChange}
        style={{
          position: 'fixed',
          left: menuContext?.x ?? lastPosRef.current.x,
          top: menuContext?.y ?? lastPosRef.current.y,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          border: 0,
          padding: 0,
        }}
      />
    </>
  );

  return { onCellContextMenu, contextMenu, getRowStyle, clearRowColors };
}
